(function () {
  const SIZE_NAMES = ['size', 'bed size', 'mattress size'];
  const INCLUDE_NAMES = [
    'pieces',
    'piece',
    'set',
    'set includes',
    'includes',
    'configuration',
    'package',
    'package includes',
    'group',
    'title'
  ];

  const AMBIGUOUS_VALUES = [
    'default title',
    'black',
    'white',
    'gray',
    'grey',
    'brown',
    'blue',
    'beige',
    'cream',
    'fabric',
    'leather',
    'wood'
  ];

  function normalizeText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function normalizeKey(value) {
    return normalizeText(value).toLowerCase();
  }

  function isSizeName(name) {
    return SIZE_NAMES.includes(normalizeKey(name));
  }

  function isIncludeName(name) {
    return INCLUDE_NAMES.includes(normalizeKey(name));
  }

  function normalizeSize(value) {
    const text = ` ${normalizeKey(value).replace(/-/g, ' ')} `;
    if (text.includes(' california king ') || text.includes(' cal king ')) return 'California King';
    if (text.includes(' twin xl ') || text.includes(' twin extra long ')) return 'Twin XL';
    if (text.includes(' queen ')) return 'Queen';
    if (text.includes(' full ')) return 'Full';
    if (text.includes(' twin ')) return 'Twin';
    if (text.includes(' king ')) return 'King';
    return '';
  }

  function titleCaseWords(value) {
    return normalizeText(value)
      .split(' ')
      .map((word) => {
        if (!word) return '';
        const lower = word.toLowerCase();
        if (lower === 'xl') return 'XL';
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  }

  function toLowerSmallWords(value) {
    return normalizeText(value).replace(/\b(With|And|For|Of|The)\b/g, (word) => word.toLowerCase());
  }

  function getCompactSingleItem(item, productTitle) {
    const normalizedItem = normalizeKey(item);
    const normalizedTitle = normalizeKey(productTitle);
    if (!normalizedItem || !normalizedTitle) return item;

    const sectionalMatch = normalizeText(productTitle).match(
      /\b(\d+\s*-\s*piece\s+sectional(?:\s+with\s+(?:left|right)\s+chaise|\s+with\s+chaise)?)\b/i
    );

    if (
      sectionalMatch
      && normalizedItem.includes('sectional')
      && normalizedItem.includes('chaise')
      && normalizedTitle.includes('sectional')
      && normalizedTitle.includes('chaise')
    ) {
      return toLowerSmallWords(titleCaseWords(sectionalMatch[1].replace(/\s*-\s*/g, '-')));
    }

    return item;
  }

  function splitParts(value) {
    let text = normalizeText(value);
    text = text.replace(/\s*&\s*/g, ' and ');
    text = text.replace(/\s*\+\s*/g, '|');
    text = text.replace(/\s*;\s*/g, '|');
    text = text.replace(/\s*,\s*/g, '|');
    text = text.replace(/\s+\band\b\s+/gi, '|');
    return text.split('|').map(normalizeText).filter(Boolean);
  }

  function stripPiecePrefix(value) {
    const text = normalizeText(value);
    const colonMatch = text.match(/^(\d+)\s*(?:-| )?(?:piece|pc|pieces)?\s*:\s*(.+)$/i);
    if (colonMatch) {
      return { explicitCount: Number(colonMatch[1]), source: colonMatch[2] };
    }

    const pieceMatch = text.match(/^(\d+)\s*(?:-| )?(?:piece|pc|pieces)\s+(.+)$/i);
    if (pieceMatch && /[,;+]|\band\b/i.test(pieceMatch[2])) {
      return { explicitCount: Number(pieceMatch[1]), source: pieceMatch[2] };
    }

    return { explicitCount: null, source: text };
  }

  function extractQuantity(part) {
    const match = normalizeText(part).match(/^(\d+)\s+(.+)$/);
    if (!match) return { quantity: 1, label: normalizeText(part) };
    return { quantity: Number(match[1]), label: normalizeText(match[2]) };
  }

  function hasWord(text, word) {
    return new RegExp(`\\b${word}\\b`, 'i').test(text);
  }

  function normalizeBedroomItem(part, bedSize) {
    const original = normalizeText(part);
    const key = normalizeKey(original).replace(/night\s*stand/g, 'nightstand');
    const { quantity, label } = extractQuantity(key);
    let item = '';

    if (key === 'bed' || key.endsWith(' bed')) {
      const alreadySized = normalizeSize(label);
      item = alreadySized ? titleCaseWords(label) : `${bedSize || ''} Bed`.trim();
    } else if (key === 'dresser mirror') {
      item = 'Dresser Mirror';
    } else if (key === 'nightstand') {
      item = 'Nightstand';
    } else if (key === 'dresser') {
      item = 'Dresser';
    } else if (key === 'mirror') {
      item = 'Mirror';
    } else if (key === 'chest') {
      item = 'Chest';
    } else if (key === 'vanity stool') {
      item = 'Vanity Stool';
    } else if (key === 'vanity') {
      item = 'Vanity';
    } else if (key === 'bench') {
      item = 'Bench';
    }

    if (!item) return null;
    return `${quantity} ${item}`;
  }

  function normalizeDiningItem(part, contextText) {
    const { quantity, label } = extractQuantity(part);
    const key = normalizeKey(label).replace(/counterheight/g, 'counter height');
    const isCounter = /counter height/i.test(contextText);
    const isBar = /\bbar\b|bar height|bar stool/i.test(contextText);
    let item = '';

    if (key === 'table' || key === 'dining table') {
      item = isCounter ? 'Counter-Height Dining Table' : isBar ? 'Bar-Height Table' : 'Dining Table';
    } else if (key === 'counter height table') {
      item = 'Counter-Height Dining Table';
    } else if (key === 'bar table') {
      item = 'Bar-Height Table';
    } else if (key === 'chair' || key === 'chairs') {
      item = quantity === 1 ? 'Dining Chair' : 'Dining Chairs';
    } else if (key === 'side chair' || key === 'side chairs') {
      item = quantity === 1 ? 'Side Chair' : 'Side Chairs';
    } else if (key === 'arm chair' || key === 'arm chairs') {
      item = quantity === 1 ? 'Arm Chair' : 'Arm Chairs';
    } else if (key === 'counter height chair' || key === 'counter height chairs') {
      item = quantity === 1 ? 'Counter-Height Chair' : 'Counter-Height Chairs';
    } else if (key === 'bar stool' || key === 'bar stools') {
      item = quantity === 1 ? 'Bar Stool' : 'Bar Stools';
    } else if (key === 'stool' || key === 'stools') {
      item = quantity === 1 ? 'Stool' : 'Stools';
    } else if (key === 'bench') {
      item = 'Dining Bench';
    } else if (key === 'server') {
      item = 'Dining Server';
    }

    if (!item) return null;
    return `${quantity} ${item}`;
  }

  function normalizeLivingItem(part) {
    const { quantity, label } = extractQuantity(part);
    const key = normalizeKey(label);
    let item = '';

    if (key === 'sofa') item = 'Sofa';
    else if (key === 'loveseat') item = 'Loveseat';
    else if (key === 'chair') item = 'Chair';
    else if (key === 'accent chair') item = 'Accent Chair';
    else if (key === 'recliner') item = 'Recliner';
    else if (key === 'ottoman') item = 'Ottoman';

    if (!item) return null;
    return `${quantity} ${item}`;
  }

  function normalizeOutdoorItem(part) {
    const { quantity, label } = extractQuantity(part);
    const key = normalizeKey(label);
    let item = '';

    if (key === 'outdoor sofa' || key === 'sofa') item = 'Outdoor Sofa';
    else if (key === 'outdoor sectional') item = 'Outdoor Sectional';
    else if (key === 'chair' || key === 'chairs' || key === 'outdoor chair' || key === 'outdoor chairs') {
      item = quantity === 1 ? 'Outdoor Chair' : 'Outdoor Chairs';
    } else if (key === 'coffee table' || key === 'outdoor coffee table') {
      item = 'Outdoor Coffee Table';
    } else if (key === 'table' || key === 'outdoor table') {
      item = 'Outdoor Table';
    } else if (key === 'ottoman') {
      item = 'Outdoor Ottoman';
    }

    if (!item) return null;
    return `${quantity} ${item}`;
  }

  function normalizeVanityItem(part) {
    const { quantity, label } = extractQuantity(part);
    const key = normalizeKey(label);
    let item = '';

    if (key === 'vanity' || key === 'vanity table') item = 'Vanity';
    else if (key === 'mirror') item = 'Mirror';
    else if (key === 'stool' || key === 'vanity stool') item = 'Vanity Stool';
    else if (key === 'bench') item = 'Bench';

    if (!item) return null;
    return `${quantity} ${item}`;
  }

  function inferCategory(productData, source) {
    const text = normalizeKey([
      productData.productType,
      productData.productTitle,
      (productData.tags || []).join(' '),
      source
    ].join(' '));

    if (text.includes('outdoor') || text.includes('patio')) return 'outdoor';
    if (text.includes('dining') || text.includes('side chair') || text.includes('bar stool') || text.includes('counter height')) return 'dining';
    if (text.includes('bedroom') || hasWord(text, 'bed') || text.includes('nightstand') || text.includes('dresser')) return 'bedroom';
    if (text.includes('living room') || text.includes('sofa') || text.includes('loveseat') || text.includes('recliner')) return 'living';
    if (text.includes('vanity')) return 'vanity';
    return 'generic';
  }

  function parseItems(sourceValue, bedSize, productData) {
    const stripped = stripPiecePrefix(sourceValue);
    const source = stripped.source;
    if (!source || normalizeKey(source) === 'default title') return null;
    if (!/[,;+]|\band\b/i.test(source)) return null;
    if (AMBIGUOUS_VALUES.includes(normalizeKey(source))) return null;

    const category = inferCategory(productData, source);
    const parts = splitParts(source);
    const items = [];

    parts.forEach((part) => {
      let item = null;
      if (category === 'bedroom') item = normalizeBedroomItem(part, bedSize);
      else if (category === 'dining') item = normalizeDiningItem(part, source);
      else if (category === 'living') item = normalizeLivingItem(part);
      else if (category === 'outdoor') item = normalizeOutdoorItem(part);
      else if (category === 'vanity') item = normalizeVanityItem(part);
      if (item) items.push(item);
    });

    if (items.length < 2) return null;
    const calculatedCount = items.reduce((sum, item) => {
      const match = item.match(/^(\d+)\s+/);
      return sum + (match ? Number(match[1]) : 1);
    }, 0);

    return {
      items,
      explicitCount: stripped.explicitCount,
      calculatedCount,
      category
    };
  }

  function getVariantOptionMap(variant, optionNames) {
    const map = {};
    (optionNames || []).forEach((name, index) => {
      map[normalizeKey(name)] = variant.options[index];
    });
    return map;
  }

  function findSize(variant, optionNames) {
    const map = getVariantOptionMap(variant, optionNames);
    for (const [name, value] of Object.entries(map)) {
      if (isSizeName(name)) {
        const size = normalizeSize(value);
        if (size) return size;
      }
    }
    for (const value of variant.options || []) {
      const size = normalizeSize(value);
      if (size) return size;
    }
    return '';
  }

  function findSourceValues(variant, optionNames) {
    const sources = [];
    (optionNames || []).forEach((name, index) => {
      const value = variant.options[index];
      if (!value) return;
      if (isIncludeName(name) && !isSizeName(name)) sources.push(value);
    });

    if (variant.title && variant.title !== 'Default Title' && /[,;+]|\band\b|:/i.test(variant.title)) {
      sources.push(variant.title);
    }

    return sources;
  }

  function generateHeading(result, bedSize, explicitHeading) {
    if (explicitHeading) return normalizeText(explicitHeading);
    const count = result.explicitCount || result.calculatedCount;

    if (result.category === 'bedroom') {
      if (count && bedSize) return `Your ${count}-Piece ${bedSize} Set`;
      if (bedSize) return `Your ${bedSize} Bedroom Set`;
      return count ? `Your ${count}-Piece Bedroom Set` : 'Your Bedroom Set';
    }

    if (result.category === 'dining') {
      return count ? `Your ${count}-Piece Dining Set` : 'Your Dining Set';
    }

    if (result.category === 'living') {
      return count ? `Your ${count}-Piece Living Room Set` : 'Your Living Room Set';
    }

    if (result.category === 'outdoor') return 'Your Outdoor Set';
    if (result.category === 'vanity') return 'Your Vanity Set';
    return 'Your Set Includes';
  }

  function cleanMetafieldList(value) {
    if (!Array.isArray(value)) return [];
    return value.map(normalizeText).filter(Boolean);
  }

  function buildContent(variant, productData, options = {}) {
    if (!variant) return null;

    const overrideItems = cleanMetafieldList(variant.itemsIncluded);
    const notIncluded = cleanMetafieldList(variant.itemsNotIncluded);
    const bedSize = findSize(variant, productData.optionNames);

    if (overrideItems.length) {
      return {
        heading: normalizeText(variant.heading) || 'Your Set Includes',
        items: overrideItems,
        notIncluded
      };
    }

    if (!options.enableAutoParse) return null;

    const sources = findSourceValues(variant, productData.optionNames);
    for (const source of sources) {
      const result = parseItems(source, bedSize, productData);
      if (result) {
        return {
          heading: generateHeading(result, bedSize, variant.heading),
          items: result.items,
          notIncluded
        };
      }
    }

    return null;
  }

  function renderList(listEl, items, productTitle) {
    listEl.innerHTML = '';
    items.forEach((item) => {
      const displayItem = items.length === 1 && window.matchMedia('(max-width: 749.98px)').matches
        ? getCompactSingleItem(item, productTitle)
        : item;
      const li = document.createElement('li');
      const icon = document.createElement('span');
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '✓';
      li.append(icon, document.createTextNode(displayItem));
      listEl.append(li);
    });
  }

  function renderSoldSeparately(el, items, show) {
    el.hidden = !show || !items.length;
    el.innerHTML = '';
    if (el.hidden) return;

    const label = document.createElement('span');
    label.textContent = 'Sold separately:';
    el.append(label, document.createTextNode(` ${items.join(', ')}`));
  }

  class ProductWhatsIncluded extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const dataEl = this.querySelector('[data-whats-included-variants]');
      if (!dataEl) return;

      this.data = JSON.parse(dataEl.textContent);
      this.cache = new Map();
      this.headingEl = this.querySelector('[data-whats-included-heading]');
      this.listEl = this.querySelector('[data-whats-included-list]');
      this.soldEl = this.querySelector('[data-whats-included-sold-separately]');
      this.enableAutoParse = this.dataset.autoParse !== 'false';
      this.showSoldSeparately = this.dataset.showSoldSeparately !== 'false';
      this.variantPicker = this.closest('.product-info')?.querySelector('variant-picker');
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.compactViewport = window.matchMedia('(max-width: 749.98px)');

      if (this.variantPicker) {
        this.variantPicker.addEventListener('on:variant:change', this.handleVariantChange);
      }
      window.addEventListener('popstate', this.handlePopState);
      if (this.compactViewport.addEventListener) {
        this.compactViewport.addEventListener('change', this.handleViewportChange);
      }

      this.updateByVariantId(this.getInitialVariantId());
    }

    disconnectedCallback() {
      if (this.variantPicker) {
        this.variantPicker.removeEventListener('on:variant:change', this.handleVariantChange);
      }
      window.removeEventListener('popstate', this.handlePopState);
      if (this.compactViewport?.removeEventListener) {
        this.compactViewport.removeEventListener('change', this.handleViewportChange);
      }
      this.initialized = false;
    }

    getInitialVariantId() {
      const params = new URLSearchParams(window.location.search);
      return Number(params.get('variant')) || Number(this.data.selectedVariantId);
    }

    getVariant(variantId) {
      return (this.data.variants || []).find((variant) => Number(variant.id) === Number(variantId));
    }

    getContent(variant) {
      if (!variant) return null;
      if (!this.cache.has(variant.id)) {
        this.cache.set(variant.id, buildContent(variant, this.data, { enableAutoParse: this.enableAutoParse }));
      }
      return this.cache.get(variant.id);
    }

    handleVariantChange(event) {
      this.updateByVariantId(event.detail?.variant?.id);
    }

    handlePopState() {
      window.setTimeout(() => this.updateByVariantId(this.getInitialVariantId()), 0);
    }

    handleViewportChange() {
      this.updateByVariantId(this.currentVariantId);
    }

    updateByVariantId(variantId) {
      this.currentVariantId = variantId;
      const variant = this.getVariant(variantId);
      const content = this.getContent(variant);
      if (!content || !content.items.length) {
        this.hidden = true;
        this.classList.remove('product-whats-included--single');
        if (this.headingEl) this.headingEl.textContent = '';
        if (this.listEl) this.listEl.innerHTML = '';
        if (this.soldEl) this.soldEl.hidden = true;
        return;
      }

      this.hidden = false;
      this.classList.toggle('product-whats-included--single', content.items.length === 1);
      this.headingEl.textContent = content.heading;
      renderList(this.listEl, content.items, this.data.productTitle);
      renderSoldSeparately(this.soldEl, content.notIncluded, this.showSoldSeparately);
    }
  }

  window.DecorWhatsIncludedParser = {
    buildContent,
    parseItems,
    normalizeSize,
    splitParts,
    getCompactSingleItem
  };

  if (!customElements.get('product-whats-included')) {
    customElements.define('product-whats-included', ProductWhatsIncluded);
  }
})();
